import { memo, useEffect } from 'react';
import toast from 'react-hot-toast';
import { PendingNotification } from '@/components/Molecules/NotificationsList';
import { useNotificationSSE } from '@/hooks';
import { useRemovePendingNotifications } from '@/hooks/service-hooks/notifications';
import { useAuthStore } from '@/store';

/**
 * Memoized component that handles SSE connection for real-time notifications.
 * This component is memoized to prevent reconnection on parent re-renders.
 */
const NotificationSSEListener = memo(() => {
  const user = useAuthStore((state) => state.user);
  const userId = user?.userInformation?._id;

  const { mutate: removePending } = useRemovePendingNotifications();

	// Dismiss all toasts when user logs out or listener unmounts
	useEffect(() => {
		if (!userId) {
			toast.dismissAll();
		}
		return () => {
			toast.dismissAll();
		};
	}, [userId]);

  const handleItemClick = (notificationId: string) => {
    if (notificationId) {
      window.location.href = `/notifications/${notificationId}`;
    }
  };

  // Set up SSE connection for live notifications
  useNotificationSSE({
    onNewNotification: (data) => {
      // Show toast notification for new notifications
      if (data.type !== 'connected') {
        toast.custom(
          (t) => (
            <PendingNotification
              onClick={handleItemClick}
              item={data}
              toastId={t.id}
              mt={3}
            />
          ),
          {
            duration: 10000,
            position: 'top-right',
          },
        );
        // Remove pending notification after showing toast
        if (userId) {
          removePending(userId);
        }
      }
    },
    onError: (error) => {
      console.error('SSE connection error:', error);
    },
    enabled: true,
  });

  // This component doesn't render anything visible
  return null;
});

NotificationSSEListener.displayName = 'NotificationSSEListener';

export default NotificationSSEListener;
